import { useEffect, useState } from "react";
import { debounce } from "../../helpers";
import AirdropNFT from "./AirdropNFT";
import NFTCard from "./NFTCard";
import { message, Select, Spin } from "antd";
import { BlockchainType, MintNFTRequestType, NFTAirdropAllocationType, NFTAirdropType, NFTType, UserMintedType } from "../../type";
import {
  getAirdropNFT,
  getNFTGenesisCollection,
  getNFTWhitelist,
  getRequestMint,
  getUserGenesisCollection,
  updateMintedNFT,
  updateTransactionNFT,
} from "../../api/apiNFT";
import { useAccount, useSwitchChain, useWriteContract } from "wagmi";
import NoDataCard from "../../components/common/nodata-card";
import { AirdropNFTEnum, NFTMintedState } from "../../type/enum";
import abiNFT from './abi/abiNFT.json';
import abiNFTAirdrop from './abi/abiNFTAirdrop.json';
import { useContract } from "../../components/utils/useContract";
import { waitForTransactionReceipt } from "@wagmi/core";
import { wagmiConfig } from "../../config/wagmi";
import { getChainById } from "../../api/apiChain";

const PER_PAGE = 10;

const NFT = () => {
  const [searchValue, setSeacrhValue] = useState("");
  const [airdropNFTs, setAirdropNFTs] = useState<NFTAirdropType[]>([]);
  const [mintNFT, setMintNFT] = useState<NFTType>();
  const [mintedNFT, setMintedNFT] = useState<UserMintedType>();
  const [targetNFTChain, setTargetNFTChain] = useState<BlockchainType>();
  const [filter, setFilter] = useState(AirdropNFTEnum.All);
  const [showNetworkWarning, setShowNetworkWarning] = useState(false);
  const { switchChainAsync, status: switchChainStatus } = useSwitchChain();
  const { address, chainId: currentChainId } = useAccount();
  const [whitelist, setWhitelist] = useState<{
    id: number;
    address: string;
  }>();

  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);
  const [loadingOnchain, setLoadingOnchain] = useState(false);
  const nftContract = useContract(mintNFT?.mint_contract, abiNFT);
  const { writeContractAsync } = useWriteContract();
  const debouncedSearchKey = debounce((value: string) => {
    setSeacrhValue(value);
  }, 1000);

  const handleSwitchChain = async (chainId: number) => {
      try {
        setLoadingOnchain(true);
        const response = await switchChainAsync({ chainId: chainId });
        if (response)
          message.success({
            content: "Switched Chain Successfully",
            className: "custom-class",
            duration: 2,
          });
      } catch (error: any) {
        if (error.name === "UserRejectedRequestError" || error.code === 4001)
          return;
        else
          message.error({
            content: "Failed to switch chain. Check your wallet connection.",
            className: "custom-class",
            duration: 2,
          });
          console.log(error);
      } finally {
        setLoadingOnchain(false);
        setShowNetworkWarning(false);
      }
  };

  //get nft card data
  const handleGetNFTData = async () => {
    try {
      setLoadingCard(true);
      const response = await getNFTGenesisCollection();
      setMintNFT(response);
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoadingCard(false);
    }
  };

  //get nft minted data (check whenever the user claim nft)
  const handleNFTMintedData = async (_address: string) => {
    try {
      setLoadingCard(true);
      const response = await getUserGenesisCollection(_address);
      setMintedNFT(response);
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoadingCard(false);
    }
  };

  //check whether the user is in whitelist
  const handleGetNFTWhitelist = async (_address: string) => {
    try {
      setLoadingCard(true);
      const response = await getNFTWhitelist(_address);
      setWhitelist(response);
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoadingCard(false);
    }
  };

  const handleGetAirdropNFT = async (
    _address: string,
    _page: number,
    _searchKey: string,
    type: AirdropNFTEnum
  ) => {
    try {
      setLoadingTable(true);
      const response = await getAirdropNFT(
        _address,
        _page,
        PER_PAGE,
        _searchKey,
        type
      );
      if (response.data) {
        setAirdropNFTs(response.data);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoadingTable(false);
    }
  };

  const handleMintNFTClick = async (_address: string) => {
    try {
      const mintNFTChain = await getChainById(mintNFT?.chain_id ?? 0);
      if (switchChainStatus !== "idle" && switchChainStatus !== "success") return;
      if (mintNFTChain.chain_id !== currentChainId) { 
        setTargetNFTChain(mintNFTChain);
        setShowNetworkWarning(true);
        return;
      }

      setLoadingOnchain(true);
      const response = await getRequestMint(_address);

      //Mint integration
      await mintNFTCall(response);

    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoadingOnchain(false);
    }
  };

  const handleClaimAirdropNFT = async (nft: NFTAirdropType) => {
    try {
      if (switchChainStatus !== "idle" && switchChainStatus !== "success") return;
      if (nft.token.chain.chain_id !== currentChainId) {
        setTargetNFTChain(nft.token.chain);
        setShowNetworkWarning(true);
        return;
      }
      setLoadingOnchain(true);
      // Claim NFT Airdrop Integration
      await claimNFTAirdrop(nft.allocation); 
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred!",
      });
      console.log(error);
    } finally {
      setLoadingOnchain(false);
    }
  };

  const handleUserClaimedNFTAirdrop = async (id: number, txn: string) => {
    try {
        await updateTransactionNFT(id, {hash: txn});

        if (address) await handleGetAirdropNFT(address, 1, searchValue, filter);
    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred when update claimed NFT!",
      })
      console.log(error);
    }
  }

  const handleUserMintedNFT = async () => {
    try {
      const response = await updateMintedNFT(address as string);
      setMintedNFT(response);
      setMintNFT(response.nft_collection);

    } catch (error) {
      message.open({
        type: "error",
        content: "An error occurred when update minted NFT!",
      })
      console.log(error);
    }
    
  }

  // Integrate claim NFT Airdrop 
  const claimNFTAirdrop = async ({id, token_address, amount_string, signature, claim_contract}: NFTAirdropAllocationType) => {
    try {
      // @ts-ignore
      const txHash = await writeContractAsync({
          abi: abiNFTAirdrop,
          address: claim_contract as `0x${string}`,
          functionName: 'claimAir',
          args: [id, token_address, amount_string, signature]
        });
      const receipt = await waitForTransactionReceipt(wagmiConfig, {hash: txHash});
      if (receipt.status === "success") {
            await handleUserClaimedNFTAirdrop(id, txHash);
            message.success({
              type: "success",
              content: "Claimed NFT successfully",
              className: "custom-class",
              duration: 2,
            });
          }

  } catch (error: any) {
      const errorCode = error?.code;
      const errorMessage = error?.message || "";

      if (
        errorCode === 4001 ||
        errorCode === 'ACTION_REJECTED' ||
        error.name === "UserRejectedRequestError" ||
        errorMessage.toLowerCase().includes("user rejected")
      ) {
        message.warning({
          type: "warning",
          content: "Transaction rejected.",
          className: "custom-class",
          duration: 2,
        });
      } else {
        message.error({
          type: "error",
          content: "An error occurred when claiming NFT Airdrop!",
          className: "custom-class",
          duration: 2,
        });
        console.error("Claim NFT Airdrop Error:", error);
      }
    }
  }

    //Integrate Mint NFT
    const mintNFTCall = async (mintNFTRequest: MintNFTRequestType) => {
        try {
          if (!nftContract) {
            message.error({
                type: "error",
                content: "Contract Error",
                className: "custom-class",
                duration: 2,
              });
              return;
            }
           
          const tx = await nftContract?.mintNFT(
            mintNFTRequest.id, 
            mintNFTRequest.campaign_id, 
            mintNFTRequest.start_time, 
            mintNFTRequest.end_time, 
            mintNFTRequest.signature);

          const receipt = await tx.wait();

          if (receipt?.status === 1) {
            await handleUserMintedNFT();
            message.success({
              type: "success",
              content: "Minted NFT successfully",
              className: "custom-class",
              duration: 2,
            });
          } else {
            message.error({
              type: "error",
              content: "Minted Failed: Transaction reverted",
              className: "custom-class",
              duration: 2,
            });
          }

    } catch (error: any) {
      const errorCode = error?.code;
      const errorMessage = error?.message || "";

      if (
        errorCode === 4001 ||
        errorCode === 'ACTION_REJECTED' ||
        error.name === "UserRejectedRequestError" ||
        (error as string).toLowerCase().includes("user rejected") ||
        errorMessage.toLowerCase().includes("user rejected")
      ) {
        message.warning({
          type: "warning",
          content: "Transaction rejected.",
          className: "custom-class",
          duration: 2,
        });
      } else {
        message.error({
          type: "error",
          content: "An error occurred when minting NFT!",
          className: "custom-class",
          duration: 2,
        });
        console.error("Mint NFT Error:", error);
      }
    }
    }

  useEffect(() => {
    handleGetNFTData();
  }, []);

  useEffect(() => {
    if (address) handleGetNFTWhitelist(address);
  }, [address]);

  useEffect(() => {
    if (whitelist && address) handleNFTMintedData(address);
  }, [whitelist, address]);

  useEffect(() => {
    if (address) handleGetAirdropNFT(address, 1, searchValue, filter);
  }, [address, filter, searchValue]);

  return (
    <>
      {showNetworkWarning && address && (
        <div className="network-warning ">
          <div className="content-warning">
            <img src="/images/red-warning.svg" alt="Warning" />
            <div className="text-warning">
              Please switch to {targetNFTChain?.name}.
              {loadingOnchain ? (
                <button className="btn-re dis">
                  Waiting
                  <div className="btn-ab dis"></div>
                </button>
              ) : (
                // Fallback to BNB Chain if the chain_id is undefined
                <button className="btn-re" onClick={() => handleSwitchChain(targetNFTChain?.chain_id ?? 97)}>
                  Switch Now
                  <div className="btn-ab"></div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {loadingOnchain && (
        <div className="fixed inset-0 z-50 bg-white/70 flex items-center justify-center pointer-events-auto">
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <div className=" pt-[87px]">
        <div className="max-w-[1420px] mx-auto px-[5vw] lg:px-10 my-10">
          <div className="font-Azonix text-[31px] sm:text-4xl xl:text-[44px] 2xl:text-[52px] text-text-primary text-left whitespace-pre-line block">
            MINT NFT
          </div>
          <div className="mt-5 lg:mt-10">
            <NFTCard
              loading={loadingCard}
              nftData={mintNFT}
              handleMintNFTClick={handleMintNFTClick}
              mintState={
                whitelist
                  ? mintedNFT
                    ? NFTMintedState.MINTED
                    : NFTMintedState.CLAIMABLE
                  : NFTMintedState.NOT_wHITELIST
              }
              mintedId={mintedNFT?.nft_id || ""}
            />
          </div>
          <div className="mt-5 lg:mt-10 font-Azonix text-[31px] xs:text-4xl sm:text-[44px] xl:text-[52px] text-text-primary text-left whitespace-pre-line block">
            AIRDROP NFT
          </div>
          <div className="mt-5 lg:mt-10 flex justify-between items-center gap-5 flex-col sm:flex-row">
            <div className="self-start search-input max-[840px]:!w-full">
              <input
                type="text"
                placeholder="Search by project/token name"
                className="f-search max-[840px]:!w-full !pr-14"
                onChange={(event) => {
                  debouncedSearchKey(event.currentTarget.value);
                }}
              />
              <div className="icon">
                <img src="./images/icon-search.png" alt="" />
              </div>
            </div>
            <div className="self-end flex items-center min-[420px]:gap-1 sm:gap-2 font-Montserrat">
              <style>
                {`
                .custom-antd-select .ant-select-selection-item {
                  font-family: Montserrat;
                  font-weight: 500;
                  letter-spacing: 0.65px;
                }
              `}
              </style>
              <Select
                placeholder="Status"
                value={filter}
                onChange={setFilter}
                options={Object.entries(AirdropNFTEnum).map(
                  ([label, value]) => ({
                    label,
                    value,
                  })
                )}
                className="font-semibold text-base w-36 custom-antd-select"
              />
            </div>
          </div>
          <div className="mt-5 w-full">
            {airdropNFTs.length > 0 && airdropNFTs ? (
              <>
                <div className="w-full bg-backgroundWhite p-4">
                  <AirdropNFT
                    airdropNFTs={airdropNFTs}
                    loading={loadingTable}
                    handleClaimAirdropNFT={handleClaimAirdropNFT}
                  />
                </div>
                <div className="w-full h-auto flex justify-end">
                  <img
                    src="/images/line-open.png"
                    alt=""
                    className="max-w-full w-[500px] h-auto"
                  />
                </div>
              </>
            ) : (
              <NoDataCard />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NFT;
